import { useState, type FormEvent } from 'react';
import { Plus, Scale } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components/ui/Dialog';
import { toast } from '@/stores/toastStore';
import { etatApi, type ReglesPaie } from '@/api/pointage';

/**
 * Pointage P4 — règles de paie, datées comme les horaires (docs/PLAN-POINTAGE.md).
 * Décisions PDG du 16/10/2026 : absence retenue au salaire ÷ 26 ; prix de l'heure supplémentaire = salaire ÷ 26 ÷ 8 h ;
 * majorations à fixer plus tard (après avis du cabinet) — vides = heures comptées, non chiffrées.
 */
const dateFr = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { timeZone: 'UTC' });
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const pct = (v: number | null) => (v == null ? 'Selon décision PDG' : `${v} %`);

export const ReglesPaiePointage = ({ estDaf }: { estDaf: boolean }) => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['hr', 'pointage', 'regles-paie'], queryFn: etatApi.regles });
  const [form, setForm] = useState(false);
  const enVigueur = q.data?.find((r) => r.enVigueur) ?? null;
  return (
    <div className="ds-table-panel">
      <div className="ds-table-header">
        <div>
          <div className="ds-table-title"><Scale size={14} style={{ verticalAlign: -2 }} /> Règles de paie</div>
          <div className="ds-table-sub">Utilisées par l'état mensuel. De nouvelles règles s'appliquent à partir de leur date.</div>
        </div>
        {estDaf && <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setForm(true)}><Plus size={14} /> Nouvelles règles</button>}
      </div>
      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 13 }}>
        {q.isLoading ? 'Chargement…' : !enVigueur ? 'Aucune règle en vigueur.' : <>
          <Info titre="Retenue d'un jour d'absence non justifiée" valeur={`Salaire ÷ ${enVigueur.diviseurAbsence}`} />
          <Info titre="Retard" valeur="Seulement noté (aucune retenue)" />
          <Info titre="Prix de l'heure supplémentaire" valeur={`Salaire ÷ ${enVigueur.diviseurAbsence} ÷ heures de la journée`} />
          <Info titre="Majoration heures sup. (semaine)" valeur={pct(enVigueur.majorationHsPct)} />
          <Info titre="Majoration heures sup. (dimanche, férié)" valeur={pct(enVigueur.majorationHsReposPct)} />
          <Info titre="En vigueur depuis le" valeur={dateFr(enVigueur.valableDu!)} />
        </>}
      </div>
      {form && <FormRegles modele={enVigueur} onClose={() => setForm(false)}
        onSaved={() => { setForm(false); void qc.invalidateQueries({ queryKey: ['hr', 'pointage'] }); }} />}
    </div>
  );
};

const Info = ({ titre, valeur }: { titre: string; valeur: string }) => (
  <div><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{titre}</div><div style={{ fontWeight: 600 }}>{valeur}</div></div>
);

const FormRegles = ({ modele, onClose, onSaved }: { modele: ReglesPaie | null; onClose: () => void; onSaved: () => void }) => {
  const [valableDu, setValableDu] = useState(aujourdhui());
  const [diviseur, setDiviseur] = useState(String(modele?.diviseurAbsence ?? ''));
  const [maj, setMaj] = useState(modele?.majorationHsPct != null ? String(modele.majorationHsPct) : '');
  const [majRepos, setMajRepos] = useState(modele?.majorationHsReposPct != null ? String(modele.majorationHsReposPct) : '');
  const m = useMutation({
    mutationFn: () => etatApi.creerRegles({
      valableDu, diviseurAbsence: Number(diviseur),
      majorationHsPct: maj === '' ? null : Number(maj), majorationHsReposPct: majRepos === '' ? null : Number(majRepos),
    }),
    onSuccess: () => { toast.success('Règles enregistrées', `À partir du ${dateFr(valableDu)}`); onSaved(); },
  });
  const soumettre = (e: FormEvent) => { e.preventDefault(); if (Number(diviseur) >= 1) m.mutate(); };
  return (
    <Dialog open onClose={onClose} title="Nouvelles règles de paie" subtitle="Elles s'appliqueront à partir de la date choisie. Laissez une majoration vide tant qu'elle n'est pas décidée.">
      <form onSubmit={soumettre} style={{ display: 'grid', gap: 12 }}>
        <label style={{ fontSize: 12 }}>À partir du<input type="date" className="ds-fi" min={aujourdhui()} value={valableDu} onChange={(e) => setValableDu(e.target.value)} required /></label>
        <label style={{ fontSize: 12 }}>Diviseur d'un jour d'absence (salaire ÷ …)<input type="number" min={1} max={31} className="ds-fi" value={diviseur} onChange={(e) => setDiviseur(e.target.value)} required /></label>
        <label style={{ fontSize: 12 }}>Majoration des heures supplémentaires en semaine (%)<input type="number" min={0} max={300} step="0.01" className="ds-fi" value={maj} onChange={(e) => setMaj(e.target.value)} placeholder="Vide = selon décision PDG" /></label>
        <label style={{ fontSize: 12 }}>Majoration le dimanche et les jours fériés (%)<input type="number" min={0} max={300} step="0.01" className="ds-fi" value={majRepos} onChange={(e) => setMajRepos(e.target.value)} placeholder="Vide = selon décision PDG" /></label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={m.isPending}>Enregistrer</button>
        </div>
      </form>
    </Dialog>
  );
};
